import React, { useEffect, useState } from 'react';
import { Settings, Sparkles, Loader2, Home } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { mccPost } from './mccApi';
import { SectionHeader } from './mccUi';
import { PLATFORMS, PROJECT_STAGES, IDEA_STAGES, REQUEST_STAGES, FILMING_STAGES, CONTENT_TYPES, statusTone } from './mccConstants';

/**
 * Settings & Administration. Now includes a proper Demo Mode toggle
 * that switches the whole tool between the live account and a demo account.
 */
export default function MccSettings() {
  const [demoOn, setDemoOn] = useState(() => localStorage.getItem('mcc-demo-mode') === 'on');
  const [busy, setBusy] = useState(false);

  const toggleDemo = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!demoOn) {
        // Turning ON: make sure demo data exists (idempotent seeder).
        await mccPost('/marketing/demo/seed', {});
        localStorage.setItem('mcc-demo-mode', 'on');
        // Also flag lender-packs demo mode on so the same toggle covers both tools if desired.
        toast.success('Demo Mode ON — you are now in the demo account.');
      } else {
        localStorage.setItem('mcc-demo-mode', 'off');
        toast.success('Demo Mode OFF — returned to your live account.');
      }
      // Small delay so the toast is seen before reload.
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      toast.error(e.message || 'Failed to toggle Demo Mode');
      setBusy(false);
    }
  };

  const wipeDemoData = async () => {
    if (!window.confirm('Delete every demo record from this workspace? Real data is untouched.')) return;
    setBusy(true);
    try {
      await mccPost('/marketing/demo/reset', {});
      localStorage.removeItem('mcc-demo-mode');
      toast.success('Demo data wiped.');
      setTimeout(() => window.location.reload(), 400);
    } catch (e) { toast.error(e.message || 'Reset failed'); setBusy(false); }
  };

  return (
    <div className="p-6 space-y-6">
      <SectionHeader
        title="Marketing Settings & Administration"
        subtitle="Reference of the platforms, stages, content types and roles this system uses. Admin-only workflow changes will land here."
        icon={Settings}
      />

      {/* Demo Mode */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-indigo-700 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Demo Mode
            </p>
            <p className="text-base font-bold text-slate-900 mt-0.5">
              {demoOn ? 'You are inside the Demo Account' : 'You are on your Live Account'}
            </p>
            <p className="text-sm text-slate-600 mt-1 max-w-lg">
              Flip this on to preview the Marketing Command Centre populated with a rich, lived-in dataset — great for demos, training and screenshots. Flipping off returns you to your live account with your real work exactly as you left it.
            </p>
          </div>
          <label
            className="inline-flex items-center gap-3 select-none flex-shrink-0 cursor-pointer"
            data-testid="mcc-demo-toggle"
          >
            <span className={`text-xs font-bold uppercase tracking-wider ${demoOn ? 'text-emerald-700' : 'text-slate-500'}`}>
              {busy ? 'Working…' : demoOn ? 'On' : 'Off'}
            </span>
            <button
              type="button"
              onClick={toggleDemo}
              disabled={busy}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition disabled:opacity-60 ${
                demoOn ? 'bg-emerald-500' : 'bg-slate-300'
              }`}
              role="switch"
              aria-checked={demoOn}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  demoOn ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
              {busy && (
                <Loader2 className="w-3 h-3 animate-spin absolute left-1/2 -translate-x-1/2 text-white" />
              )}
            </button>
          </label>
        </div>
        {demoOn && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={wipeDemoData} disabled={busy}
              className="text-rose-700 border-rose-200 hover:bg-rose-50">
              Wipe demo dataset entirely
            </Button>
          </div>
        )}
      </div>

      <Card title="Platforms">
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map((p) => (
            <span key={p.v} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ background: p.tone }}>
              {p.l}
            </span>
          ))}
        </div>
      </Card>
      <Card title="Content types">
        <div className="flex flex-wrap gap-2">
          {CONTENT_TYPES.map((c) => (
            <span key={c.v} className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800">{c.l}</span>
          ))}
        </div>
      </Card>
      <Card title="Project workflow"><StageRow stages={PROJECT_STAGES} /></Card>
      <Card title="Ideas workflow"><StageRow stages={IDEA_STAGES} /></Card>
      <Card title="Requests workflow"><StageRow stages={REQUEST_STAGES} /></Card>
      <Card title="Filming workflow"><StageRow stages={FILMING_STAGES} /></Card>

      <Card title="Roles">
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <Role role="Broker Requester" desc="Submits marketing requests, uploads supporting files, comments on their own items." />
          <Role role="Content Team" desc="Manages ideas & requests, creates projects, prepares scripts/hooks, assigns editors, publishes." />
          <Role role="Editor" desc="Downloads raw footage, uploads draft & final versions, responds to feedback." />
          <Role role="Manager / Approver" desc="Reviews ideas & requests, leaves timestamped feedback, approves for publishing." />
          <Role role="Admin" desc="Full access — users, roles, permissions, workflow, retention, notifications." />
        </div>
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-2">{title}</p>
      {children}
    </div>
  );
}
function StageRow({ stages }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {stages.map((s) => {
        const t = statusTone(s);
        return (
          <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: t.bg, color: t.ink }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.accent }} /> {t.label}
          </span>
        );
      })}
    </div>
  );
}
function Role({ role, desc }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
      <p className="font-bold text-slate-900">{role}</p>
      <p className="text-slate-600 mt-0.5 text-[12px]">{desc}</p>
    </div>
  );
}
